import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './drawer.css';

type DrawerSide = 'left' | 'right';
type DrawerSize = 'sm' | 'md' | 'lg' | 'full';

const sizeToWidth: Record<DrawerSize, string> = {
  sm: '360px',
  md: '480px',
  lg: '640px',
  full: '100vw',
};

const DRAWER_ANIMATION_MS = 250;

export type DrawerProps = {
  open: boolean;
  onClose: () => void;
  side?: DrawerSide;
  size?: DrawerSize;
  title?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  tabs?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  ariaLabel?: string;
  className?: string;
};

const focusable = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function Drawer({
  open,
  onClose,
  side = 'right',
  size = 'md',
  title,
  badge,
  actions,
  tabs,
  footer,
  children,
  ariaLabel,
  className,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const touchStartYRef = useRef<number | null>(null);
  const titleId = useId();
  const portalRoot = useMemo(() => (typeof document !== 'undefined' ? document.body : null), []);
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(open);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }
    setVisible(false);
    const timer = window.setTimeout(() => setMounted(false), DRAWER_ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!visible || !panelRef.current) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const first = panelRef.current.querySelector<HTMLElement>(focusable);
    (first ?? panelRef.current).focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (!panelRef.current) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = panelRef.current.querySelectorAll<HTMLElement>(focusable);
      if (!nodes.length) {
        event.preventDefault();
        return;
      }
      const firstNode = nodes[0];
      const lastNode = nodes[nodes.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === firstNode) {
        event.preventDefault();
        lastNode.focus();
      } else if (!event.shiftKey && active === lastNode) {
        event.preventDefault();
        firstNode.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [visible]);

  const handleTouchStart = (event: React.TouchEvent) => {
    if (window.innerWidth >= 768) return;
    touchStartYRef.current = event.touches[0]?.clientY ?? null;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (window.innerWidth >= 768 || touchStartYRef.current == null) return;
    const endY = event.changedTouches[0]?.clientY ?? touchStartYRef.current;
    const delta = endY - touchStartYRef.current;
    touchStartYRef.current = null;
    if (delta > 72) {
      onCloseRef.current();
    }
  };

  if (!portalRoot || !mounted) return null;

  const panelState = visible ? 'open' : 'closed';
  const labelledBy = title ? titleId : undefined;

  return createPortal(
    <>
      <div
        className="wp-drawer-overlay"
        data-state={panelState}
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        className={`wp-drawer-panel ${className ?? ''}`}
        data-side={side}
        data-state={panelState}
        style={{ ['--wp-drawer-width' as string]: sizeToWidth[size] }}
        role="dialog"
        aria-modal="true"
        aria-label={!labelledBy ? ariaLabel : undefined}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <span className="wp-drawer-handle" aria-hidden />
        <header className="wp-drawer-header">
          <div className="wp-drawer-header-main">
            {title ? (
              <h2 id={titleId} className="wp-drawer-title">
                {title}
              </h2>
            ) : null}
            {badge}
          </div>
          <div className="wp-drawer-header-actions">
            {actions}
            <button
              className="wp-drawer-close wp-focus-ring"
              type="button"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </header>
        {tabs ? <div className="wp-drawer-tabs">{tabs}</div> : null}
        <div className="wp-drawer-body">{children}</div>
        {footer ? <footer className="wp-drawer-footer">{footer}</footer> : null}
      </div>
    </>,
    portalRoot,
  );
}
